import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Grid,
  Divider,
  Avatar,
  Paper,
  useTheme,
} from "@mui/material";
import {
  AccountBalanceWallet,
  CreditCard,
  TrendingUp,
  Add,
  Download,
  Visibility,
  VisibilityOff,
  CallMade,
  CallReceived,
  Autorenew,
  DateRange,
  MoreVert,
  AccountBalance,
  CardGiftcard,
} from "@mui/icons-material";
import Layout from "../components/layout/Layout";

const MyWallet = () => {
  const theme = useTheme();
  const [showBalance, setShowBalance] = useState(true);
  const [filterType, setFilterType] = useState("all");

  // Sample wallet data
  const walletBalance = 5847.5;
  const pendingAmount = 250.0;
  const earnedThisMonth = 1250.0;

  // Sample transactions
  const transactions = [
    {
      id: 1,
      type: "credit",
      title: "Tax Consultation Payment",
      description: "Payment received from John Doe",
      amount: 150.0,
      date: "2025-10-02",
      time: "10:30 AM",
      status: "completed",
    },
    {
      id: 2,
      type: "debit",
      title: "Withdrawal to Bank",
      description: "Transfer to HDFC Bank ****4567",
      amount: 500.0,
      date: "2025-10-01",
      time: "3:45 PM",
      status: "completed",
    },
    {
      id: 3,
      type: "credit",
      title: "Financial Planning Session",
      description: "Payment received from Sarah Smith",
      amount: 200.0,
      date: "2025-09-30",
      time: "2:15 PM",
      status: "completed",
    },
    {
      id: 4,
      type: "credit",
      title: "Bookkeeping Review",
      description: "Payment received from Mike Johnson",
      amount: 100.0,
      date: "2025-09-28",
      time: "11:20 AM",
      status: "completed",
    },
    {
      id: 5,
      type: "debit",
      title: "Platform Fee",
      description: "Monthly subscription charge",
      amount: 29.99,
      date: "2025-09-25",
      time: "12:00 AM",
      status: "completed",
    },
    {
      id: 6,
      type: "credit",
      title: "Audit Preparation",
      description: "Payment pending from Emma Wilson",
      amount: 250.0,
      date: "2025-09-24",
      time: "4:30 PM",
      status: "pending",
    },
  ];

  const filteredTransactions = transactions.filter((t) => {
    if (filterType === "all") return true;
    if (filterType === "credit") return t.type === "credit";
    if (filterType === "debit") return t.type === "debit";
    return true;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Layout>
      <Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh", py: 4 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight="700" gutterBottom>
              My Wallet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your earnings and transactions
            </Typography>
          </Box>

          {/* Balance Cards Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Main Balance Card */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: "white",
                  borderRadius: 3,
                  boxShadow: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccountBalanceWallet />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Available Balance
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => setShowBalance(!showBalance)}
                      sx={{ color: "white" }}
                    >
                      {showBalance ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </Box>

                  <Typography variant="h3" fontWeight="700" sx={{ mb: 1 }}>
                    {showBalance
                      ? `₹${walletBalance.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}`
                      : "₹••••••"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.8, mb: 4, display: "block" }}
                  >
                    Updated just now
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      sx={{
                        flex: 1,
                        bgcolor: "white",
                        color: theme.palette.primary.main,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                        fontWeight: 600,
                        py: 1.5,
                      }}
                    >
                      Add Money
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CallMade />}
                      sx={{
                        flex: 1,
                        borderColor: "white",
                        color: "white",
                        "&:hover": {
                          borderColor: "white",
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                        fontWeight: 600,
                        py: 1.5,
                      }}
                    >
                      Withdraw
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Stats Cards */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar sx={{ bgcolor: "warning.light" }}>
                        <Autorenew sx={{ color: "warning.main" }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Pending
                        </Typography>
                        <Typography variant="h6" fontWeight="700">
                          ₹
                          {pendingAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar sx={{ bgcolor: "success.light" }}>
                        <TrendingUp sx={{ color: "success.main" }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          This Month
                        </Typography>
                        <Typography variant="h6" fontWeight="700">
                          ₹
                          {earnedThisMonth.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2.5,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 3 },
                  transition: "all 0.3s",
                  borderRadius: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "primary.light",
                    width: 48,
                    height: 48,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <CreditCard sx={{ color: "primary.main" }} />
                </Avatar>
                <Typography variant="body2" fontWeight="600">
                  Add Card
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2.5,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 3 },
                  transition: "all 0.3s",
                  borderRadius: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "secondary.light",
                    width: 48,
                    height: 48,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <AccountBalance sx={{ color: "secondary.main" }} />
                </Avatar>
                <Typography variant="body2" fontWeight="600">
                  Bank Account
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2.5,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 3 },
                  transition: "all 0.3s",
                  borderRadius: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "success.light",
                    width: 48,
                    height: 48,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <Download sx={{ color: "success.main" }} />
                </Avatar>
                <Typography variant="body2" fontWeight="600">
                  Statement
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2.5,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 3 },
                  transition: "all 0.3s",
                  borderRadius: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "warning.light",
                    width: 48,
                    height: 48,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <CardGiftcard sx={{ color: "warning.main" }} />
                </Avatar>
                <Typography variant="body2" fontWeight="600">
                  Rewards
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Transactions Section */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="700">
                    Recent Transactions
                  </Typography>
                  <Button
                    size="small"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    View All
                  </Button>
                </Box>

                {/* Filter Chips */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    label="All"
                    onClick={() => setFilterType("all")}
                    color={filterType === "all" ? "primary" : "default"}
                    sx={{ fontWeight: 500 }}
                  />
                  <Chip
                    label="Money In"
                    onClick={() => setFilterType("credit")}
                    color={filterType === "credit" ? "primary" : "default"}
                    sx={{ fontWeight: 500 }}
                  />
                  <Chip
                    label="Money Out"
                    onClick={() => setFilterType("debit")}
                    color={filterType === "debit" ? "primary" : "default"}
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>

              {/* Transaction List */}
              <Box>
                {filteredTransactions.map((transaction, index) => (
                  <Box key={transaction.id}>
                    <Box
                      sx={{
                        p: 3,
                        "&:hover": { bgcolor: "action.hover" },
                        transition: "all 0.2s",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flex: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor:
                                transaction.type === "credit"
                                  ? "success.light"
                                  : "error.light",
                              width: 48,
                              height: 48,
                            }}
                          >
                            {transaction.type === "credit" ? (
                              <CallReceived sx={{ color: "success.main" }} />
                            ) : (
                              <CallMade sx={{ color: "error.main" }} />
                            )}
                          </Avatar>

                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography variant="body1" fontWeight="600">
                                {transaction.title}
                              </Typography>
                              {transaction.status === "pending" && (
                                <Chip
                                  label="Pending"
                                  size="small"
                                  color="warning"
                                  sx={{ height: 20 }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              {transaction.description}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <DateRange
                                sx={{ fontSize: 14, color: "text.secondary" }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(transaction.date)} •{" "}
                                {transaction.time}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box sx={{ textAlign: "right" }}>
                            <Typography
                              variant="h6"
                              fontWeight="700"
                              color={
                                transaction.type === "credit"
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {transaction.type === "credit" ? "+" : "-"}₹
                              {transaction.amount.toFixed(2)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {transaction.status === "completed"
                                ? "Completed"
                                : "Pending"}
                            </Typography>
                          </Box>
                          <IconButton size="small">
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                    {index < filteredTransactions.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>

              {filteredTransactions.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <AccountBalanceWallet
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No transactions found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filter
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Layout>
  );
};

export default MyWallet;
